import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import {
  type TransferListOption,
  type UseTransferListReturn,
  useTransferList,
} from '../hooks/useTransferList';
import { ScrollArea } from './ScrollArea';

export interface TransferListHeaderProps {
  itemCount: number;
  selectedItemCount: number;
}

const TransferListHeader = React.memo((props: TransferListHeaderProps) => {
  return (
    <ChakraV3.Stack>
      <ChakraV3.HStack>
        <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
          Items
        </ChakraV3.Text>
        <ChakraV3.Text fontSize='sm' color='secondary'>
          {props.selectedItemCount} / {props.itemCount} selected
        </ChakraV3.Text>
      </ChakraV3.HStack>
    </ChakraV3.Stack>
  );
});

export type TransferListContentItem<A> = TransferListOption<A> & {
  isSelected: boolean;
  onClick: () => void;
};

export interface TransferListContentProps<A> {
  options: TransferListContentItem<A>[];
}

const TransferListContent = React.memo(<A,>(props: TransferListContentProps<A>) => {
  return (
    <ChakraV3.Stack>
      {props.options.map((option) => (
        <ChakraV3.HStack key={option.value}>
          <input type='checkbox' checked={option.isSelected} onChange={option.onClick} />
          <ChakraV3.Text fontSize='sm'>{option.label}</ChakraV3.Text>
        </ChakraV3.HStack>
      ))}
    </ChakraV3.Stack>
  );
});

interface TransferButtonsProps {
  onClickTransferToRight: () => void;
  onClickTransferToLeft: () => void;
  isLeftDisabled: boolean;
  isRightDisabled: boolean;
  isLeftLoading?: boolean;
  isRightLoading?: boolean;
}

const TransferButtons = React.memo((props: TransferButtonsProps) => {
  return (
    <ChakraV3.Stack h='full' align='center'>
      <ChakraV3.Stack h='full' w='1px' bg='gray.200' />
      <ChakraV3.Stack>
        <ChakraV3.IconButton
          aria-label='measurement-transfer-list-item-right'
          onClick={props.onClickTransferToRight}
          disabled={props.isRightDisabled}
          loading={props.isRightLoading}
          variant='subtle'
          size='sm'
        >
          <ChakraV3.Icon>
            <FaArrowRight />
          </ChakraV3.Icon>
        </ChakraV3.IconButton>
        <ChakraV3.IconButton
          aria-label='measurement-transfer-list-item-left'
          onClick={props.onClickTransferToLeft}
          disabled={props.isLeftDisabled}
          loading={props.isLeftLoading}
          variant='subtle'
          size='sm'
        >
          <ChakraV3.Icon>
            <FaArrowLeft />
          </ChakraV3.Icon>
        </ChakraV3.IconButton>
      </ChakraV3.Stack>
      <ChakraV3.Stack h='full' w='1px' bg='gray.200' />
    </ChakraV3.Stack>
  );
});

export interface TransferListComponents<A> {
  left?: {
    Header?: (props: TransferListHeaderProps) => React.ReactElement | null;
    Content?: (props: TransferListContentProps<A>) => React.ReactElement | null;
  };
  right?: {
    Header?: (props: TransferListHeaderProps) => React.ReactElement | null;
    Content?: (props: TransferListContentProps<A>) => React.ReactElement | null;
  };
}

export const TransferListComponents = {
  left: {
    Header: TransferListHeader,
    Content: TransferListContent,
  },
  right: {
    Header: TransferListHeader,
    Content: TransferListContent,
  },
};

const getComponents = <A,>(components: Partial<TransferListComponents<A>>) => {
  return {
    left: {
      ...TransferListComponents.left,
      ...components.left,
    },
    right: {
      ...TransferListComponents.right,
      ...components.right,
    },
  };
};

export interface TransferListProps<A> {
  options: TransferListOption<A>[];
  value: TransferListOption<A>[];
  onChange: (values: TransferListOption<A>[]) => void;
  components?: TransferListComponents<A>;
  isLeftLoading?: boolean;
  isRightLoading?: boolean;
}

export const TransferList = <A,>(props: TransferListProps<A>) => {
  const { components } = props;

  const transferList: UseTransferListReturn<A> = useTransferList(props);

  const Components = React.useMemo(() => getComponents(components ?? {}), [components]);

  return (
    <ChakraV3.HStack w='full' h='full'>
      <ChakraV3.Stack h='full'>
        {Components.left.Header({
          itemCount: transferList.leftOptions.length,
          selectedItemCount: transferList.leftOptions.filter((o) => o.isSelected).length,
        })}
        <ChakraV3.Separator />
        <Components.left.Content options={transferList.leftOptions} />
      </ChakraV3.Stack>

      <TransferButtons
        onClickTransferToRight={transferList.onClickTransferToRight}
        onClickTransferToLeft={transferList.onClickTransferToLeft}
        isLeftDisabled={!transferList.rightOptions.some((o) => o.isSelected)}
        isRightDisabled={!transferList.leftOptions.some((o) => o.isSelected)}
        isLeftLoading={transferList.isLeftLoading}
        isRightLoading={transferList.isRightLoading}
      />

      <ChakraV3.Stack h='full' flexGrow={1}>
        {Components.right.Header({
          itemCount: transferList.rightOptions.length,
          selectedItemCount: transferList.rightOptions.filter((o) => o.isSelected).length,
        })}
        <ChakraV3.Separator />
        <ScrollArea w='full' h='full' overflowY='auto'>
          <Components.right.Content options={transferList.rightOptions} />
        </ScrollArea>
      </ChakraV3.Stack>
    </ChakraV3.HStack>
  );
};
